// ProfilePage.tsx - remove unused queryClient
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { User, Mail, Shield, CreditCard, Bell } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuthStore } from '../store/authStore';
import api from '../lib/api';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

export default function ProfilePage() {
  const { user, updateUser } = useAuthStore();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
  });

  const updateMutation = useMutation({
    mutationFn: (data: any) => api.put('/auth/profile', data),
    onSuccess: (response) => {
      const updatedUser = response.data.data.user;
      updateUser(updatedUser);
      setIsEditing(false);
      toast.success('Profile updated successfully');
    },
    onError: () => {
      toast.error('Failed to update profile');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate(formData);
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'pro': return 'bg-blue-500';
      case 'premium': return 'bg-purple-500';
      case 'enterprise': return 'bg-amber-500';
      default: return 'bg-gray-500';
    }
  };

  const getTierFeatures = (tier: string) => {
    const features = {
      free: ['2 comparisons', '10 portfolio positions', '2 courses'],
      pro: ['3 comparisons', '50 positions', '3 courses', '10 alerts', 'Analytics'],
      premium: ['Unlimited comparisons', 'Unlimited positions', '4 courses', '50 alerts', 'API access'],
      enterprise: ['All features', 'Team accounts', '5 courses', 'Unlimited alerts', 'White-label'],
    };
    return features[tier as keyof typeof features] || features.free;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">Profile Settings</h1>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl shadow-lg p-8 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <div className="w-20 h-20 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-full flex items-center justify-center mr-4">
                <User className="w-10 h-10 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{user?.firstName} {user?.lastName}</h2>
                <p className="text-gray-600">{user?.email}</p>
              </div>
            </div>
            {!isEditing && <button onClick={() => setIsEditing(true)} className="btn btn-secondary">Edit Profile</button>}
          </div>

          {isEditing ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                  <input type="text" value={formData.firstName} onChange={(e) => setFormData({ ...formData, firstName: e.target.value })} className="input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                  <input type="text" value={formData.lastName} onChange={(e) => setFormData({ ...formData, lastName: e.target.value })} className="input" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="input" />
              </div>
              <div className="flex gap-3">
                <button type="submit" className="btn btn-primary">Save Changes</button>
                <button type="button" onClick={() => setIsEditing(false)} className="btn btn-secondary">Cancel</button>
              </div>
            </form>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <div className="flex items-center text-gray-600 mb-2">
                  <Mail className="w-4 h-4 mr-2" />
                  Email Status
                </div>
                <div className="flex items-center">
                  {user?.emailVerified ? (
                    <span className="text-green-600 flex items-center"><Shield className="w-4 h-4 mr-1" />Verified</span>
                  ) : (
                    <span className="text-yellow-600">Not verified</span>
                  )}
                </div>
              </div>
              {user?.stellarPublicKey && (
                <div>
                  <div className="flex items-center text-gray-600 mb-2"><Shield className="w-4 h-4 mr-2" />Stellar Wallet</div>
                  <div className="text-sm font-mono text-gray-900">
                    {user.stellarPublicKey.substring(0, 8)}...{user.stellarPublicKey.substring(user.stellarPublicKey.length - 8)}
                  </div>
                </div>
              )}
            </div>
          )}
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white rounded-2xl shadow-lg p-8 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <CreditCard className="w-6 h-6 text-gray-600 mr-2" />
              <h3 className="text-xl font-semibold text-gray-900">Subscription</h3>
            </div>
            <span className={`${getTierColor(user?.subscriptionTier || 'free')} text-white px-4 py-2 rounded-full font-semibold`}>
              {user?.subscriptionTier?.toUpperCase() || 'FREE'}
            </span>
          </div>
          <div className="mb-6">
            <h4 className="font-medium text-gray-900 mb-3">Your Features</h4>
            <ul className="space-y-2">
              {getTierFeatures(user?.subscriptionTier || 'free').map((feature, i) => (
                <li key={i} className="flex items-center text-gray-600">
                  <div className="w-1.5 h-1.5 bg-primary-600 rounded-full mr-2"></div>
                  {feature}
                </li>
              ))}
            </ul>
          </div>
          {user?.subscriptionTier === 'free' ? (
            <Link to="/pricing" className="btn btn-primary w-full">Upgrade Now</Link>
          ) : (
            <div className="flex gap-3">
              <Link to="/pricing" className="btn btn-secondary flex-1">Change Plan</Link>
              <button className="btn btn-ghost flex-1">Manage Billing</button>
            </div>
          )}
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white rounded-2xl shadow-lg p-8">
          <div className="flex items-center mb-6">
            <Bell className="w-6 h-6 text-gray-600 mr-2" />
            <h3 className="text-xl font-semibold text-gray-900">Notifications</h3>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b">
              <div>
                <div className="font-medium text-gray-900">Email Notifications</div>
                <div className="text-sm text-gray-600">Receive updates about your portfolio</div>
              </div>
              <input type="checkbox" className="toggle" defaultChecked />
            </div>
            <div className="flex items-center justify-between py-3 border-b">
              <div>
                <div className="font-medium text-gray-900">Price Alerts</div>
                <div className="text-sm text-gray-600">Get notified when prices change</div>
              </div>
              <input type="checkbox" className="toggle" defaultChecked />
            </div>
            <div className="flex items-center justify-between py-3">
              <div>
                <div className="font-medium text-gray-900">Educational Content</div>
                <div className="text-sm text-gray-600">New courses and lessons</div>
              </div>
              <input type="checkbox" className="toggle" />
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
